/**
 * TemplateEditorPage – Full-page template create/edit (replaces drawer).
 *
 * Route: /dashboard/settings/documentation/template/new | /dashboard/settings/documentation/template/:templateId
 * Back navigates to /dashboard/settings/documentation with unsaved-changes guard.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Container,
  Group,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconAlertTriangle, IconArrowLeft, IconSettings } from '@tabler/icons-react';
import type { Questionnaire, QuestionnaireItem } from '@medplum/fhirtypes';
import type { TemplateCategory, TemplateListItem } from './types';
import {
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_CATEGORIES,
  STATUS_TO_FHIR,
  type ScoreCalculationMethod,
} from './types';
import {
  setTemplateCategory,
  stripRetiredTemplateServiceTypeTags,
  getScoreRanges,
  setScoreRanges,
  getQuestionnaireMaxScore,
  getTreatmentPlanDefaultDiagnosisCode,
  setTreatmentPlanDefaultDiagnosisCode,
} from './utils/templateTags';
import { validateQuestionnaire, type ValidationError } from './utils/questionnaireValidation';
import { validateTreatmentPlanDiagnosisSemantics } from './utils/treatmentPlanTemplateValidation';

function formatValidationMessages(issues: ValidationError[]): string {
  return issues.map((issue) => (issue.path ? `${issue.path}: ${issue.message}` : issue.message)).join('\n');
}

function showTemplateSaveErrorToast(title: string, message: string): void {
  notifications.show({
    icon: <IconAlertCircle size={18} />,
    title,
    message,
    color: 'red',
    withCloseButton: true,
    withBorder: true,
    autoClose: 8000,
    styles: { root: { borderColor: '#9CA3AF' } },
  });
}

function showTemplateSaveWarningToast(message: string): void {
  notifications.show({
    icon: <IconAlertTriangle size={18} />,
    title: 'Warnings',
    message,
    color: 'yellow',
    withCloseButton: true,
    withBorder: true,
    autoClose: 8000,
    styles: { root: { borderColor: '#9CA3AF' } },
  });
}
import {
  hasSignatureRequired,
  applySignatureRequiredExtension,
} from '../../../lib/template-setup/questionnaire';
import { isMockMedplumMode } from '../../../lib/isMockMedplumMode';
import { isStandardDiagnosisTreatmentPlanTemplate } from '../../../lib/chart/chartDocumentTemplates';
import { useTemplateCRUD, questionnaireToTemplateListItem } from './hooks';
import { getMockTemplates, isMockTemplateId } from './mockTemplates';
import {
  generateLocalId,
  getLocalTemplate,
  isLocalTemplateId,
  saveLocalTemplate,
} from './localTemplateStore';
import { AssessmentScoringModal } from './components/AssessmentScoringModal';
import { DocumentBuilder, ensureQuestionnaireItemKeys, FORM_HEADER_SELECTION, sanitizeQuestionnaireItemTree } from './components/DocumentBuilder';
import { PreviewFormContent } from './components/TemplatePreviewDrawer';
import { getIcd10Description } from './data/icd10MentalBehavioral';
import { TREATMENT_PLAN_COMMON_DIAGNOSES } from './data/treatmentPlanCommonDiagnoses';
import type { ScoringConfiguration } from './scoringTypes';
import { getScoringConfig, setScoringConfig } from './utils/scoringUtils';
import { emptyScoringConfig } from './data/mockScoringData';
import {
  DOCUMENTATION_SETTINGS_PATH,
  normalizeTemplateLibraryTab,
  resolveTemplateLibraryReturnTab,
  templateLibraryListPath,
  type TemplateLibraryTab,
} from './templateLibraryUiState';

export function TemplateEditorPage(): JSX.Element {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const crud = useTemplateCRUD();

  const isNew = !templateId || templateId === 'new';
  const categoryFromQuery = searchParams.get('category');
  const normalizedQueryTab = normalizeTemplateLibraryTab(categoryFromQuery);
  const defaultCategory: TemplateCategory =
    normalizedQueryTab && normalizedQueryTab !== 'settings' ? normalizedQueryTab : 'administrative';
  const initialTemplateFromState = location.state?.template as TemplateListItem | undefined;
  const isMock = !!templateId && isMockTemplateId(templateId) && isMockMedplumMode();
  const isLocal = !!templateId && isLocalTemplateId(templateId);
  const mockFromList = isMock && templateId ? getMockTemplates().find((t) => t.id === templateId) : undefined;
  const localFromStore = isLocal && templateId ? getLocalTemplate(templateId) : undefined;
  const initialTemplate = initialTemplateFromState ?? localFromStore ?? mockFromList ?? null;

  const [template, setTemplate] = useState<TemplateListItem | null>(initialTemplate);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew && !initialTemplate);

  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [draftStatus, setDraftStatus] = useState<'draft' | 'active'>('active');
  const [draftCategory, setDraftCategory] = useState<TemplateCategory>(defaultCategory);
  const [draftItems, setDraftItems] = useState<QuestionnaireItem[]>([]);
  const [draftRequireSignature, setDraftRequireSignature] = useState(false);
  /** Score ranges for Measures only. Total is computed from template items. Good: 0..goodMax (green), Ok: goodMax+1..okMax (yellow), Bad: okMax+1..totalMax (red). */
  const [draftScoreRangeGoodMax, setDraftScoreRangeGoodMax] = useState<number | ''>('');
  const [draftScoreRangeOkMax, setDraftScoreRangeOkMax] = useState<number | ''>('');
  /** How total score is calculated (e.g. sum). Only 'sum' is persisted today; UI supports future methods. */
  const [draftScoreCalculationMethod, setDraftScoreCalculationMethod] = useState<ScoreCalculationMethod>('sum');
  const [scoringModalOpened, setScoringModalOpened] = useState(false);
  /** FHIR-native scoring configuration (outputs, bands, risk flags). */
  const [draftScoringConfig, setDraftScoringConfig] = useState<ScoringConfiguration>(emptyScoringConfig());
  /** Default diagnosis code for Treatment Planning templates. Description is derived from code. */
  const [draftDiagnosisCode, setDraftDiagnosisCode] = useState<string>('');

  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('edit');
  const [previewKey, setPreviewKey] = useState(0);
  const [isStarterContent, setIsStarterContent] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmCloseOpened, setConfirmCloseOpened] = useState(false);
  const [saving, setSaving] = useState(false);

  const initialSnapshot = useRef<string>('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const libraryReturnTab = useRef<TemplateLibraryTab>(
    resolveTemplateLibraryReturnTab({
      returnTabFromState: location.state?.returnTab,
      categoryFromQuery: searchParams.get('category'),
      fallback: defaultCategory,
    }),
  );

  const navigateToLibrary = useCallback(() => {
    navigate(templateLibraryListPath(libraryReturnTab.current));
  }, [navigate]);

  // Load template when editing (skip fetch for mock – use state or run effect once)
  useEffect(() => {
    if (isNew) {
      setTemplate(null);
      setDraftTitle('');
      setDraftDescription('');
      setDraftStatus('active');
      setDraftCategory(defaultCategory);
      setDraftItems([]);
      setDraftRequireSignature(false);
      setDraftScoreRangeGoodMax('');
      setDraftScoreRangeOkMax('');
      setDraftDiagnosisCode('');
      setDraftScoringConfig(emptyScoringConfig());
      setLoadError(null);
      setLoading(false);
      initialSnapshot.current = JSON.stringify({
        title: '',
        description: '',
        status: 'active',
        category: defaultCategory,
        items: [],
        requireSignature: false,
        scoreRangeGoodMax: '',
        scoreRangeOkMax: '',
        diagnosisCode: '',
        scoringConfig: emptyScoringConfig(),
      });
      return;
    }
    if ((isMock || isLocal) && initialTemplate) {
      const listItem = initialTemplate;
      const ranges = getScoreRanges(listItem.resource);
      setTemplate(listItem);
      setDraftTitle(listItem.title);
      setDraftDescription(listItem.description);
      setDraftStatus(listItem.status === 'retired' ? 'active' : (listItem.status as 'draft' | 'active'));
      setDraftCategory(listItem.category);
      setDraftScoreRangeGoodMax(ranges.goodMax ?? '');
      setDraftScoreRangeOkMax(ranges.okMax ?? '');
      setDraftDiagnosisCode(getTreatmentPlanDefaultDiagnosisCode(listItem.resource) ?? '');
      setDraftRequireSignature(hasSignatureRequired(listItem.resource));
      const scoringCfg = getScoringConfig(listItem.resource);
      setDraftScoringConfig(scoringCfg);
      const items = ensureQuestionnaireItemKeys(listItem.resource.item) ?? [];
      setDraftItems(items);
      initialSnapshot.current = JSON.stringify({
        title: listItem.title,
        description: listItem.description,
        status: listItem.status,
        category: listItem.category,
        items,
        requireSignature: hasSignatureRequired(listItem.resource),
        scoreRangeGoodMax: ranges.goodMax ?? '',
        scoreRangeOkMax: ranges.okMax ?? '',
        diagnosisCode: getTreatmentPlanDefaultDiagnosisCode(listItem.resource) ?? '',
        scoringConfig: scoringCfg,
      });
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    crud
      .fetchTemplate(templateId!)
      .then((q) => {
        if (cancelled) return;
        const listItem = questionnaireToTemplateListItem(q);
        const ranges = getScoreRanges(q);
        setTemplate(listItem);
        setDraftTitle(listItem.title);
        setDraftDescription(listItem.description);
        setDraftStatus(listItem.status === 'retired' ? 'active' : (listItem.status as 'draft' | 'active'));
        setDraftCategory(listItem.category);
        setDraftScoreRangeGoodMax(ranges.goodMax ?? '');
        setDraftScoreRangeOkMax(ranges.okMax ?? '');
        setDraftDiagnosisCode(getTreatmentPlanDefaultDiagnosisCode(q) ?? '');
        setDraftRequireSignature(hasSignatureRequired(q));
        const scoringCfg = getScoringConfig(q);
        setDraftScoringConfig(scoringCfg);
        const items = ensureQuestionnaireItemKeys(q.item) ?? [];
        setDraftItems(items);
        initialSnapshot.current = JSON.stringify({
          title: listItem.title,
          description: listItem.description,
          status: listItem.status,
          category: listItem.category,
          items,
          requireSignature: hasSignatureRequired(q),
          scoreRangeGoodMax: ranges.goodMax ?? '',
          scoreRangeOkMax: ranges.okMax ?? '',
          diagnosisCode: getTreatmentPlanDefaultDiagnosisCode(q) ?? '',
          scoringConfig: scoringCfg,
        });
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load template');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [templateId, isNew, isMock, isLocal, initialTemplate, defaultCategory, crud]);

  useEffect(() => {
    if (isNew || !template) return;
    if (!isStandardDiagnosisTreatmentPlanTemplate(template.resource)) return;
    navigateToLibrary();
  }, [isNew, template, navigateToLibrary]);

  useEffect(() => {
    if (location.state?.returnTab || searchParams.get('category') || !template?.category) {
      return;
    }
    libraryReturnTab.current = template.category;
  }, [template?.category, location.state, searchParams]);

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        title: draftTitle,
        description: draftDescription,
        status: draftStatus,
        category: draftCategory,
        items: draftItems,
        requireSignature: draftRequireSignature,
        scoreRangeGoodMax: draftScoreRangeGoodMax,
        scoreRangeOkMax: draftScoreRangeOkMax,
        diagnosisCode: draftDiagnosisCode,
        scoringConfig: draftScoringConfig,
      }),
    [draftTitle, draftDescription, draftStatus, draftCategory, draftItems, draftRequireSignature, draftScoreRangeGoodMax, draftScoreRangeOkMax, draftDiagnosisCode, draftScoringConfig],
  );

  /** Total score computed from template items (choice/open-choice answerOption max per item, summed). */
  const computedTotalScore = useMemo(() => getQuestionnaireMaxScore(draftItems), [draftItems]);

  useEffect(() => {
    setHasUnsavedChanges(initialSnapshot.current !== '' && currentSnapshot !== initialSnapshot.current);
  }, [currentSnapshot]);

  const goBack = useCallback(() => {
    if (hasUnsavedChanges) {
      setConfirmCloseOpened(true);
    } else {
      navigateToLibrary();
    }
  }, [hasUnsavedChanges, navigateToLibrary]);

  const handleForceClose = useCallback(() => {
    setConfirmCloseOpened(false);
    setHasUnsavedChanges(false);
    navigateToLibrary();
  }, [navigateToLibrary]);

  const handleClearStarter = useCallback(() => {
    setDraftItems([]);
    setIsStarterContent(false);
  }, []);

  const handleSave = useCallback(async () => {
    if (template && isStandardDiagnosisTreatmentPlanTemplate(template.resource)) {
      return;
    }
    if (!draftTitle.trim()) {
      notifications.show({
        icon: <IconAlertTriangle size={18} />,
        title: 'Template title required',
        message: 'Please add a title to your template before saving.',
        color: 'yellow',
        withCloseButton: true,
        withBorder: true,
        autoClose: 4000,
        styles: { root: { borderColor: '#9CA3AF' } },
      });
      return;
    }
    let questionnaire: Questionnaire = {
      resourceType: 'Questionnaire',
      title: draftTitle.trim(),
      description: draftDescription.trim() || undefined,
      status: STATUS_TO_FHIR[draftStatus],
      item: draftItems.length > 0 ? sanitizeQuestionnaireItemTree(draftItems) : undefined,
    };
    const isMockTemplate = template && isMockTemplateId(template.id);
    const isLocalTemplate = template && isLocalTemplateId(template.id);
    if (!isNew && template && !isMockTemplate && !isLocalTemplate) {
      const prev = template.resource;
      questionnaire = {
        ...questionnaire,
        id: prev.id,
        meta: prev.meta,
        url: prev.url,
        name: prev.name,
        identifier: prev.identifier,
      };
    }
    questionnaire = setTemplateCategory(questionnaire, draftCategory);
    questionnaire = stripRetiredTemplateServiceTypeTags(questionnaire);
    questionnaire = applySignatureRequiredExtension(questionnaire, draftRequireSignature);
    if (draftCategory === 'treatment-planning') {
      questionnaire = setTreatmentPlanDefaultDiagnosisCode(questionnaire, draftDiagnosisCode || undefined);
    }
    if (draftCategory === 'measures') {
      const totalMax = computedTotalScore ?? undefined;
      const goodMax = typeof draftScoreRangeGoodMax === 'number' ? draftScoreRangeGoodMax : undefined;
      const okMax = typeof draftScoreRangeOkMax === 'number' ? draftScoreRangeOkMax : undefined;
      if (goodMax !== undefined && okMax !== undefined && okMax <= goodMax) {
        showTemplateSaveErrorToast(
          'Validation errors',
          'scoreRanges: Ok (yellow) max must be higher than Good (green) max.',
        );
        return;
      }
      if (totalMax !== undefined || goodMax !== undefined || okMax !== undefined) {
        questionnaire = setScoreRanges(questionnaire, { totalMax, goodMax, okMax });
      }
      questionnaire = setScoringConfig(questionnaire, draftScoringConfig);
    }

    const validation = [
      ...validateQuestionnaire(questionnaire),
      ...(draftStatus === 'active' ? validateTreatmentPlanDiagnosisSemantics(questionnaire) : []),
    ];
    const validationErrors = validation.filter((issue) => issue.severity === 'error');
    const validationWarnings = validation.filter((issue) => issue.severity === 'warning');
    if (validationErrors.length > 0) {
      showTemplateSaveErrorToast('Validation errors', formatValidationMessages(validationErrors));
      return;
    }

    setSaving(true);
    try {
      const isRealTemplate = !isNew && template && !isMockTemplate && !isLocalTemplate;

      let saved: Questionnaire;
      if (isRealTemplate) {
        saved = await crud.updateTemplate(questionnaire);
      } else if (isNew) {
        saved = await crud.createTemplate(questionnaire, draftCategory);
      } else {
        // Mock or local template — save to local store only
        const localId = isLocalTemplate && template ? template.id : generateLocalId();
        saved = { ...questionnaire, id: localId };
        const localItem: TemplateListItem = {
          id: localId,
          title: saved.title ?? 'Untitled',
          description: saved.description ?? '',
          status: draftStatus as 'draft' | 'active',
          category: draftCategory,
          lastUpdated: new Date().toISOString(),
          versionId: '1',
          isDefault: false,
          inUse: false,
          resource: saved,
        };
        saveLocalTemplate(localItem);
      }

      const id = saved.id ?? template?.id ?? 'unknown';
      const listItem = questionnaireToTemplateListItem(saved);
      setTemplate(listItem);

      initialSnapshot.current = currentSnapshot;
      setHasUnsavedChanges(false);

      notifications.show({
        title: 'Template saved',
        message: `"${saved.title ?? 'Untitled'}" has been saved successfully.`,
        color: 'green',
        withCloseButton: true,
        withBorder: true,
        autoClose: 4000,
        styles: { root: { borderColor: '#9CA3AF' } },
      });

      if (validationWarnings.length > 0) {
        showTemplateSaveWarningToast(formatValidationMessages(validationWarnings));
      }

      navigate(`${DOCUMENTATION_SETTINGS_PATH}/template/${id}`, { replace: true });
    } catch (err) {
      showTemplateSaveErrorToast(
        'Save error',
        err instanceof Error ? err.message : 'Failed to save template',
      );
    } finally {
      setSaving(false);
    }
  }, [
    draftTitle,
    draftDescription,
    draftStatus,
    draftCategory,
    draftItems,
    draftRequireSignature,
    computedTotalScore,
    draftScoreRangeGoodMax,
    draftScoreRangeOkMax,
    draftDiagnosisCode,
    draftScoringConfig,
    isNew,
    template,
    currentSnapshot,
    navigate,
    crud,
  ]);

  const draftQuestionnaire = useMemo((): Questionnaire => {
    let q: Questionnaire = {
      resourceType: 'Questionnaire',
      id: template?.id ?? 'draft-preview',
      title: draftTitle.trim() || 'Untitled',
      description: draftDescription.trim() || undefined,
      status: 'draft',
      item: draftItems.length > 0 ? draftItems : undefined,
    };
    q = applySignatureRequiredExtension(q, draftRequireSignature);
    return q;
  }, [template?.id, draftTitle, draftDescription, draftItems, draftRequireSignature]);

  const isEditing = !isNew && !!template;

  if (loading) {
    return (
      <Container size="lg" py="xl">
        <Text c="dimmed">Loading template…</Text>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Error" withCloseButton onClose={navigateToLibrary}>
          {loadError}
        </Alert>
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={navigateToLibrary} mt="md">
          Back to Template Library
        </Button>
      </Container>
    );
  }

  return (
    <>
      <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
        <Box
          px="lg"
          py="sm"
          style={{
            borderBottom: '1px solid var(--mantine-color-gray-3)',
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 'var(--mantine-spacing-md)',
            backgroundColor: 'var(--mantine-color-white)',
          }}
        >
          <Group gap="sm" justify="flex-start">
            <Button
              variant="subtle"
              color="gray"
              size="compact-sm"
              leftSection={<IconArrowLeft size={16} />}
              onClick={goBack}
            >
              Back
            </Button>
            {draftCategory === 'measures' && (
              <Tooltip label="Assessment scoring settings">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  onClick={() => setScoringModalOpened(true)}
                  aria-label="Assessment scoring settings"
                >
                  <IconSettings size={18} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
          <Group gap="md" justify="center">
            <Title order={3}>{draftTitle || (isEditing ? template?.title : 'New Template')}</Title>
            <SegmentedControl
              value={viewMode}
              onChange={(v) => {
                const mode = v as 'edit' | 'preview';
                if (mode === 'preview') {
                  setPreviewKey((k) => k + 1);
                }
                setViewMode(mode);
              }}
              data={[
                { label: 'Edit', value: 'edit' },
                { label: 'Preview', value: 'preview' },
              ]}
              size="xs"
            />
          </Group>
          <Group gap="sm" justify="flex-end">
            <Select
              data={TEMPLATE_CATEGORIES.map((c) => ({ value: c, label: TEMPLATE_CATEGORY_LABELS[c] }))}
              value={draftCategory}
              onChange={(v) => setDraftCategory((v as TemplateCategory) ?? defaultCategory)}
              allowDeselect={false}
              w={200}
            />
            <Select
              data={[
                { value: 'draft', label: 'Draft' },
                { value: 'active', label: 'Active' },
              ]}
              value={draftStatus}
              onChange={(v) => setDraftStatus((v as 'draft' | 'active') ?? 'draft')}
              allowDeselect={false}
              w={200}
            />
            <Tooltip label="When enabled, this template requires a client signature">
              <Switch
                label="Require signature"
                checked={draftRequireSignature}
                onChange={(e) => setDraftRequireSignature(e.currentTarget.checked)}
                size="sm"
                styles={{ label: { fontSize: 'var(--mantine-font-size-xs)' } }}
              />
            </Tooltip>
            <Button onClick={handleSave} loading={saving}>
              Save
            </Button>
          </Group>
        </Box>

        <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box
            ref={scrollContainerRef}
            style={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              padding: 'var(--mantine-spacing-lg)',
              minWidth: 0,
            }}
          >
            <Container size="md" px={0}>
              <Stack gap="md">
                {viewMode === 'edit' ? (
                  <>
                    <DocumentBuilder
                      title={draftTitle}
                      onTitleChange={setDraftTitle}
                      description={draftDescription}
                      onDescriptionChange={setDraftDescription}
                      items={draftItems}
                      onItemsChange={(items) => {
                        setDraftItems(items);
                        if (isStarterContent) setIsStarterContent(false);
                      }}
                      scrollContainerRef={scrollContainerRef}
                      initialSelectedLinkId={isNew ? FORM_HEADER_SELECTION : null}
                      treatmentPlanDiagnosis={
                        draftCategory === 'treatment-planning'
                          ? {
                              code: draftDiagnosisCode,
                              onCodeChange: setDraftDiagnosisCode,
                              description:
                                draftDiagnosisCode
                                  ? (TREATMENT_PLAN_COMMON_DIAGNOSES.find((o) => o.code === draftDiagnosisCode)
                                      ?.description ?? getIcd10Description(draftDiagnosisCode) ?? '')
                                  : '',
                              options: TREATMENT_PLAN_COMMON_DIAGNOSES,
                            }
                          : undefined
                      }
                    />
                    {isStarterContent && (
                      <Button variant="subtle" color="gray" size="compact-xs" onClick={handleClearStarter} w="fit-content" mx="auto" mt="md">
                        Clear starter content
                      </Button>
                    )}
                  </>
                ) : (
                  <Stack gap="lg">
                    <PreviewFormContent key={previewKey} questionnaire={draftQuestionnaire} />
                  </Stack>
                )}

              </Stack>
            </Container>
          </Box>
        </Box>
      </Box>

      {draftCategory === 'measures' && (
        <AssessmentScoringModal
          opened={scoringModalOpened}
          onClose={() => setScoringModalOpened(false)}
          items={draftItems}
          onItemsChange={setDraftItems}
          scoreCalculationMethod={draftScoreCalculationMethod}
          onScoreCalculationMethodChange={setDraftScoreCalculationMethod}
          computedTotalScore={computedTotalScore}
          goodMax={draftScoreRangeGoodMax}
          onGoodMaxChange={setDraftScoreRangeGoodMax}
          okMax={draftScoreRangeOkMax}
          onOkMaxChange={setDraftScoreRangeOkMax}
          scoringConfig={draftScoringConfig}
          onScoringConfigChange={setDraftScoringConfig}
        />
      )}

      <Modal opened={confirmCloseOpened} onClose={() => setConfirmCloseOpened(false)} title="Unsaved changes" centered size="sm">
        <Text size="sm">You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</Text>
        <Group wrap="nowrap" justify="flex-end" mt="lg" gap="sm">
          <Button variant="default" onClick={() => setConfirmCloseOpened(false)}>
            Keep editing
          </Button>
          <Button color="red" onClick={handleForceClose}>
            Discard changes
          </Button>
        </Group>
      </Modal>
    </>
  );
}
